import { FC, useContext } from "react";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import ClaimsActivityLineOfBusinessFilter from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityLineOfBusinessFilter";
import { LocationConfidenceScoresLabelEnum } from "@app/projects/enumerators";
import {
  CareJourneyScoresValueEnum,
  EffectivenessEfficiencyValueEnum,
  MotiveScoreValueEnum,
  NewPatientClaimsEnum
} from "@app/projects/servicing-provider";
import { locationConfidenceScoreToDisplay } from "@app/projects/utility";
import CareJourneyScoresFilter from "@app/projects/servicing-provider/provider-claims/CareJourneyScoresFilter";
import EffectivenessEfficiencyFilter from "@app/projects/servicing-provider/provider-claims/EffectivenessEfficiencyFilter";
import LocationConfidenceFilter from "@app/projects/servicing-provider/provider-claims/LocationConfidenceFilter";
import MotiveAppropriatenessFilter from "@app/projects/servicing-provider/provider-claims/MotiveAppropriatenessFilter";
import NewPatientClaimsFilter from "@app/projects/servicing-provider/provider-claims/NewPatientClaimsFilter";
import VolumeLabelFilter from "@app/projects/servicing-provider/provider-claims/VolumeLabelFilter";
import { ReportCustomizationContext } from "@app/report-customization/build-reports/ReportCustomizationContext";
import { ReportCustomizationReducerActions } from "@app/report-customization/build-reports/interfaces/reportCustomizationReducerActionsEnum";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";

interface PciFiltersSectionProps {
  showCareJourneyScores: boolean;
  showLocationConfidenceScores: boolean;
  showMotiveAppropriatenessScore: boolean;
  showTricareClaimsVolumeColumn: boolean;
  showVaClaimsVolumeColumn: boolean;
}

const mapToDropdownItems = <T extends string>(values: T[]): DropdownItem<T>[] => values.map((key) => ({ key, name: key }));

const mappedVolumeLabels = mapToDropdownItems(Object.values(ClaimsActivityVolumeLabelEnum).filter((x) => x !== ClaimsActivityVolumeLabelEnum.Total));
const mappedEffectivenessAndEfficiencyValues = mapToDropdownItems(Object.values(EffectivenessEfficiencyValueEnum));
const mappedMotiveScoreValues = mapToDropdownItems(Object.values(MotiveScoreValueEnum));
const mappedNewPatientClaimsValues = mapToDropdownItems(Object.values(NewPatientClaimsEnum));
const mappedCareJourneyCostAndQualityValues = mapToDropdownItems(Object.values(CareJourneyScoresValueEnum));
const mappedLocationConfidenceValues: DropdownItem<LocationConfidenceScoresLabelEnum>[] = Object.values(LocationConfidenceScoresLabelEnum).map((x) => ({
  key: x,
  name: locationConfidenceScoreToDisplay(x)
}));

export const PciFiltersSection: FC<PciFiltersSectionProps> = (props: PciFiltersSectionProps) => {
  const rcContext = useContext(ReportCustomizationContext);

  const selectedVolumeLabels = mapToDropdownItems(rcContext.state.selectedVolumeLabels);
  const selectedEffectivenessValues = mapToDropdownItems(rcContext.state.selectedEffectivenessValues);
  const selectedEfficiencyValues = mapToDropdownItems(rcContext.state.selectedEfficiencyValues);
  const selectedNewPatientClaimsValues = mapToDropdownItems(rcContext.state.selectedNewPatientClaimsValues);
  const selectedLocationConfidenceValues = mapToDropdownItems(rcContext.state.selectedLocationConfidenceValues);
  const selectedMotiveAppropriatenessValues = mapToDropdownItems(rcContext.state.selectedMotiveAppropriatenessValues);
  const selectedCareJourneyCostValues = mapToDropdownItems(rcContext.state.selectedCareJourneyCostValues);
  const selectedCareJourneyQualityValues = mapToDropdownItems(rcContext.state.selectedCareJourneyQualityValues);

  return (
    <>
      <div className="row mt-3">
        <h2 className="header-s col-auto">Provider Claims Insights</h2>
      </div>
      <div className="row mb-3">
        <div className="col-md-3">
          <ClaimsActivityLineOfBusinessFilter
            selectedLineOfBusiness={rcContext.state.selectedLineOfBusiness}
            setSelectedLineOfBusiness={(selectedLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum) => rcContext.dispatch({
              payload: { selectedLineOfBusiness },
              type: ReportCustomizationReducerActions.ChangeSelectedLineOfBusiness
            })}
            testId="report-customization__select--line-of-business"
            validationTooltipContent="Line of business that should be considered when identifying provider claims status"
            project={{ showTricareClaimsVolumeColumn: props.showTricareClaimsVolumeColumn, showVaClaimsVolumeColumn: props.showVaClaimsVolumeColumn }}
          />
        </div>
        <div className="col-md-3">
          <VolumeLabelFilter
            mappedVolumeLabels={mappedVolumeLabels}
            selectedVolumeLabels={selectedVolumeLabels}
            setSelectedVolumeLabels={(values: DropdownItem<ClaimsActivityVolumeLabelEnum>[]) => rcContext.dispatch({
              payload: { selectedVolumeLabels: values.map((x) => x.key) },
              type: ReportCustomizationReducerActions.ChangeSelectedVolumeLabels
            })}
            testId="report-customization__multiselect--volume-label"
          />
        </div>
        <div className="col-md-3">
          <NewPatientClaimsFilter
            mappedValues={mappedNewPatientClaimsValues}
            selectedValues={selectedNewPatientClaimsValues}
            setSelectedValues={(values: DropdownItem<NewPatientClaimsEnum>[]) => rcContext.dispatch({
              payload: { selectedNewPatientClaimsValues: values.map((x) => x.key) },
              type: ReportCustomizationReducerActions.ChangeSelectedNewPatientClaimsValues
            })}
            testId="report-customization__multiselect--new-patient-claims"
            title="New Patient Claims"
          />
        </div>
      </div>
      <div className="row mb-3">
        {props.showLocationConfidenceScores && (
          <div className="col-md-2">
            <LocationConfidenceFilter
              mappedValues={mappedLocationConfidenceValues}
              selectedValues={selectedLocationConfidenceValues}
              setSelectedValues={(values: DropdownItem<LocationConfidenceScoresLabelEnum>[]) => rcContext.dispatch({
                payload: { selectedLocationConfidenceValues: values.map((x) => x.key) },
                type: ReportCustomizationReducerActions.ChangeSelectedLocationConfidenceValues
              })}
              testId="report-customization__multiselect--location-confidence"
              title="Location Confidence"
            />
          </div>
        )}
        <div className="col-md-2">
          <EffectivenessEfficiencyFilter
            mappedValues={mappedEffectivenessAndEfficiencyValues}
            selectedValues={selectedEffectivenessValues}
            setSelectedValues={(values: DropdownItem<EffectivenessEfficiencyValueEnum>[]) => rcContext.dispatch({
              payload: { selectedEffectivenessValues: values.map((x) => x.key) },
              type: ReportCustomizationReducerActions.ChangeSelectedEffectivenessValues
            })}
            testId="report-customization__multiselect--effectiveness"
            title="Effectiveness"
          />
        </div>
        <div className="col-md-2">
          <EffectivenessEfficiencyFilter
            mappedValues={mappedEffectivenessAndEfficiencyValues}
            selectedValues={selectedEfficiencyValues}
            setSelectedValues={(values: DropdownItem<EffectivenessEfficiencyValueEnum>[]) => rcContext.dispatch({
              payload: { selectedEfficiencyValues: values.map((x) => x.key) },
              type: ReportCustomizationReducerActions.ChangeSelectedEfficiencyValues
            })}
            testId="report-customization__multiselect--efficiency"
            title="Efficiency"
          />
        </div>
        {props.showMotiveAppropriatenessScore && (
          <div className="col-md-2">
            <MotiveAppropriatenessFilter
              mappedValues={mappedMotiveScoreValues}
              selectedValues={selectedMotiveAppropriatenessValues}
              setSelectedValues={(values: DropdownItem<MotiveScoreValueEnum>[]) => rcContext.dispatch({
                payload: { selectedMotiveAppropriatenessValues: values.map((x) => x.key) },
                type: ReportCustomizationReducerActions.ChangeSelectedMotiveAppropriatenessValues
              })}
              testId="report-customization__multiselect--motive-appropriateness"
            />
          </div>
        )}
        {props.showCareJourneyScores && (
          <div className="col-md-2">
            <CareJourneyScoresFilter
              careJourneyScoreValues={mappedCareJourneyCostAndQualityValues}
              selectedValues={selectedCareJourneyCostValues}
              setSelectedValues={(values: DropdownItem<CareJourneyScoresValueEnum>[]) => rcContext.dispatch({
                payload: { selectedCareJourneyCostValues: values.map((x) => x.key) },
                type: ReportCustomizationReducerActions.ChangeSelectedCareJourneyCostValues
              })}
              testId="report-customization__multiselect--carejourney-cost"
              title="CareJourney Cost"
            />
          </div>
        )}
        {props.showCareJourneyScores && (
          <div className="col-md-2">
            <CareJourneyScoresFilter
              careJourneyScoreValues={mappedCareJourneyCostAndQualityValues}
              selectedValues={selectedCareJourneyQualityValues}
              setSelectedValues={(values: DropdownItem<CareJourneyScoresValueEnum>[]) => rcContext.dispatch({
                payload: { selectedCareJourneyQualityValues: values.map((x) => x.key) },
                type: ReportCustomizationReducerActions.ChangeSelectedCareJourneyQualityValues
              })}
              testId="report-customization__multiselect--carejourney-quality"
              title="CareJourney Quality"
            />
          </div>
        )}
      </div>
    </>
  );
}
